import { useMutation } from "@apollo/react-hooks";
import gql from "graphql-tag";
import {
  UpdateTask as UpdateTaskGenerated,
  UpdateTask_updateTask as UpdateTaskPayloadGenerated,
  UpdateTaskVariables as UpdateTaskVariablesGenerated,
  UpdateTask_updateTask_task as UpdatedTaskGenerated,
} from "core/queries/__generated__/UpdateTask";

export interface UpdateTaskPayload extends UpdateTaskPayloadGenerated {}
export interface UpdateTaskResponse extends UpdateTaskGenerated {}
export interface UpdateTaskVariables extends UpdateTaskVariablesGenerated {}
export const updateTask = gql`
  mutation UpdateTask(
    $addTags: [ID!]
    $assigneeId: String
    $assigneeIds: [String!]
    $description: String
    $dueBy: RFC3339DateTime
    $lastViewedAt: RFC3339DateTime
    $newNotes: String
    $newCleaningCompanyId: ID
    $newScheduledStartAt: RFC3339DateTime
    $priority: Int
    $recurrenceSettings: TaskRecurrenceSettingsInput
    $removeTags: [ID!]
    $status: String
    $statusUpdatedAt: RFC3339DateTime
    $taskId: ID!
    $typeStructuredMetadata: TypeStructuredMetadata
  ) {
    updateTask(
      addTags: $addTags
      newAssigneeId: $assigneeId
      newAssigneeIds: $assigneeIds
      newCleaningCompanyId: $newCleaningCompanyId
      newDueAt: $dueBy
      newLastViewedAt: $lastViewedAt
      newName: $description
      newNotes: $newNotes
      newPriority: $priority
      newScheduledStartAt: $newScheduledStartAt
      newStatus: $status
      statusUpdatedAt: $statusUpdatedAt
      recurrenceSettings: $recurrenceSettings
      removeTags: $removeTags
      tomTaskId: $taskId
      typeStructuredMetadata: $typeStructuredMetadata
    ) {
      task {
        id
        status
        priority
        name
        dueAt
        estimatedDuration
        assignee {
          id
        }
        assignees {
          id
        }
        cleaningRequest {
          id
          company {
            id
            city {
              id
            }
            name
          }
        }
        scheduledStartAt
        taskFeedItemConnections {
          lastViewedAt
          unreadCount
        }
        tags {
          id
          tagType
          name
        }
        tomTaskRecurrenceSetting {
          id
          daysOfWeek
          intervalAmt
          intervalUnit
        }
        updatedAt
      }
    }
  }
`;

interface UseUpdateTaskQueryOptions {
  awaitRefetchQueries?: boolean;
  onCompleted?: (data: UpdateTaskResponse) => void;
  optimisticResponse?: UpdateTaskResponse;
  refetchQueries?: string[];
}

type propsToPick = "id" | "dueAt" | "tomTaskRecurrenceSetting";

interface UpdateTaskOptimisticResponse
  extends Pick<UpdatedTaskGenerated, propsToPick> {}

interface UpdateTaskWithAssigneesOptimisticResponse
  extends Pick<UpdatedTaskGenerated, "id" | "assignees"> {}

export const updateTaskOptimisticResponse = (
  response:
    | UpdateTaskOptimisticResponse
    | UpdateTaskWithAssigneesOptimisticResponse,
) => ({
  updateTask: {
    task: {
      ...response,
      __typename: "Task" as UpdatedTaskGenerated["__typename"],
    },
    __typename: "UpdateTaskPayload" as UpdateTaskPayload["__typename"],
  },
});

export const useUpdateTask = ({
  awaitRefetchQueries,
  onCompleted,
  optimisticResponse,
  refetchQueries = [],
}: UseUpdateTaskQueryOptions = {}) =>
  useMutation<UpdateTaskResponse, UpdateTaskVariables>(updateTask, {
    onCompleted,
    refetchQueries,
    awaitRefetchQueries,
    optimisticResponse,
  });
